import React from "react";

import Dropdown from "../forms/dropdown/Dropdown";
import AutoComplete from "../forms/auto-complete/AutoComplete";
import AddressAutoComplete from "../forms/address/address-auto-complete/AddressAutoComplete";
import SplitDropdown from "../forms/split-dropdown/SplitDropdown";

import * as styles from "./styles/shipment-entry-input-group.module.scss";

import {
  TRANSPORTATION_MODES,
  TRANSPORTATION_MODE_OPTIONS,
  WEIGHT_UNITS,
  OCEAN_WEIGHT_UNIT_OPTIONS,
  WEIGHT_UNIT_OPTIONS,
  REEFER_OPTIONS,
  IS_INLAND_OPTIONS,
} from "./constants/dropdown-options";
import {parseAddress, parsePort} from "./utils/utils";

import {
  Address,
  Weight,
  FieldErrors,
  TransportationModeType,
  WeightUnits,
} from "./types";
import type {AddressObjectType} from "../../types";
import type {AutoCompleteDropdownOption} from "../forms/auto-complete/AutoComplete";

export interface ShipmentEntryInputData {
  transportationMode: TransportationModeType;
  originPortCode?: string;
  originPortName?: string;
  destinationPortCode?: string;
  destinationPortName?: string;
  originAddress?: Address;
  destinationAddress?: Address;
  weight: Weight;
  teus?: string;
  isLCL?: boolean;
  isReefer: boolean;
  isInland: boolean;
}

export const emptyAddress: Address = {
  street_address: "",
  street_address2: "",
  city: "",
  state: "",
  country_code: "",
  zip: "",
};

interface Props {
  onChange: (data: ShipmentEntryInputData) => void;
  data: ShipmentEntryInputData;
  fieldErrors: FieldErrors;
}

export const InitialShipmentEntryInputData: ShipmentEntryInputData = {
  transportationMode: TRANSPORTATION_MODES.oceanFcl,
  originPortCode: "",
  originPortName: "",
  destinationPortCode: "",
  destinationPortName: "",
  originAddress: emptyAddress,
  destinationAddress: emptyAddress,
  isLCL: false,
  isReefer: false,
  isInland: false,
  weight: {unit: WEIGHT_UNITS.teus, value: 0},
};

const ALGOLIA_INDEX = process.env.GATSBY_ALGOLIA_OPEN_EMISSIONS_INDEX || "";
const MIN_LETTERS_TO_SEARCH = 3;

const transportationModeTooltipContent =
  "Please select the mode of transportation";
const weightTooltipContent =
  "Enter the weight of your shipment in TEUs, lbs, or kg";
const reeferTooltipContent =
  "Select if your shipment contains refrigerated goods";
const inlandTooltipContent = "Select if your shipment is transported inland";

const ShipmentEntryInputGroup = (props: Props) => {
  const {onChange, data, fieldErrors} = props;

  const {transportationMode, weight} = data;

  const handleTransportationModeChange = React.useCallback(
    (selectedValue: string) => {
      onChange({
        ...data,
        originPortCode: "",
        destinationPortCode: "",
        originAddress: emptyAddress,
        destinationAddress: emptyAddress,
        weight:
          selectedValue === TRANSPORTATION_MODES.oceanFcl ||
          selectedValue === TRANSPORTATION_MODES.oceanLcl
            ? {unit: WEIGHT_UNITS.teus, value: 0}
            : {unit: WEIGHT_UNITS.lb, value: data.weight.value},
        transportationMode:
          TRANSPORTATION_MODES[selectedValue as TransportationModeType],
      });
    },
    [data, onChange]
  );

  const handleOriginAddressChange = React.useCallback(
    (selectedValue: string | AddressObjectType) => {
      const address = parseAddress(selectedValue);
      onChange({
        ...data,
        originAddress: address,
      });
    },
    [data, onChange]
  );

  const handleDestinationAddressChange = React.useCallback(
    (selectedValue: string | AddressObjectType) => {
      const address = parseAddress(selectedValue);
      onChange({
        ...data,
        destinationAddress: address,
      });
    },
    [data, onChange]
  );

  const handleOriginPortChange = React.useCallback(
    (selectedValue: AutoCompleteDropdownOption | null) => {
      if (selectedValue) {
        const {portCode, name} = parsePort(selectedValue);
        onChange({
          ...data,
          originPortCode: portCode,
          originPortName: name,
        });
      } else {
        onChange({
          ...data,
          originPortCode: "",
          originPortName: "",
        });
      }
    },
    [data, onChange]
  );

  const handleDestinationPortChange = React.useCallback(
    (selectedValue: AutoCompleteDropdownOption | null) => {
      if (selectedValue) {
        const {portCode, name} = parsePort(selectedValue);
        onChange({
          ...data,
          destinationPortCode: portCode,
          destinationPortName: name,
        });
      } else {
        onChange({
          ...data,
          destinationPortCode: "",
          destinationPortName: "",
        });
      }
    },
    [data, onChange]
  );

  const handleWeightValueChange = React.useCallback(
    (selectedValue: string) => {
      onChange({
        ...data,
        weight: {
          ...weight,
          value: parseFloat(selectedValue),
        },
      });
    },
    [weight, data, onChange]
  );

  const handleWeightUnitChange = React.useCallback(
    (selectedValue: string) => {
      onChange({
        ...data,
        weight: {
          ...weight,
          unit: selectedValue as WeightUnits,
        },
      });
    },
    [data, onChange, weight]
  );

  const handleReeferChange = React.useCallback(
    (selectedValue: string) => {
      onChange({
        ...data,
        isReefer: selectedValue === "true",
      });
    },
    [data, onChange]
  );

  const handleInlandChange = React.useCallback(
    (selectedValue: string) => {
      onChange({
        ...data,
        isInland: selectedValue === "true",
      });
    },
    [data, onChange]
  );

  const algoliaFilter = React.useMemo(
    () => ({
      seaport:
        transportationMode === TRANSPORTATION_MODES.oceanFcl ||
        transportationMode === TRANSPORTATION_MODES.oceanLcl,
      airport: transportationMode === TRANSPORTATION_MODES.air,
    }),
    [transportationMode]
  );

  return (
    <div>
      <form>
        <div className={styles.shipmentEntryInputGroup__mainContainer}>
          <Dropdown
            label="Mode"
            placeholder="Select"
            options={TRANSPORTATION_MODE_OPTIONS}
            onChange={handleTransportationModeChange}
            name="mode"
            transparentBg={false}
            status={fieldErrors.transportationMode ? "error" : ""}
            errorMessage={fieldErrors.transportationMode}
            value={data.transportationMode}
            tooltipContent={transportationModeTooltipContent}
            classes={styles.shipmentEntryInputGroup__modeDropdown}
          />
          {transportationMode === TRANSPORTATION_MODES.air ||
          transportationMode === TRANSPORTATION_MODES.oceanFcl ||
          transportationMode === TRANSPORTATION_MODES.oceanLcl ? (
            <div
              key={transportationMode}
              className={styles.shipmentEntryInputGroup__originDestInline}
            >
              <div className={styles.shipmentEntryInputGroup__originDest}>
                <AutoComplete
                  label="Origin Port"
                  placeholder="Address, city, or zip code"
                  name="origin"
                  onChange={handleOriginPortChange}
                  algoliaIndices={ALGOLIA_INDEX}
                  algoliaFilter={algoliaFilter}
                  status={fieldErrors.originPort ? "error" : ""}
                  errorMessage={fieldErrors.originPort}
                  hideDropdownButton
                  minLettersForSearch={MIN_LETTERS_TO_SEARCH}
                />
              </div>
              <div className={styles.shipmentEntryInputGroup__originDest}>
                <AutoComplete
                  label="Destination Port"
                  placeholder="Address, city, or zip code"
                  name="destination"
                  onChange={handleDestinationPortChange}
                  algoliaIndices={ALGOLIA_INDEX}
                  algoliaFilter={algoliaFilter}
                  status={fieldErrors.destinationPort ? "error" : ""}
                  errorMessage={fieldErrors.destinationPort}
                  hideDropdownButton
                  minLettersForSearch={MIN_LETTERS_TO_SEARCH}
                />
              </div>
            </div>
          ) : (
            <div
              key={transportationMode}
              className={styles.shipmentEntryInputGroup__originDestInline}
            >
              <div className={styles.shipmentEntryInputGroup__originDest}>
                <AddressAutoComplete
                  placeholder="Address, city, or zip code"
                  onChange={handleOriginAddressChange}
                  name="origin"
                  transparentBg={false}
                  label="Origin Address"
                  addressType="mailing"
                  sameAsMailing
                  status={fieldErrors.originAddress ? "error" : ""}
                  errorMessage={fieldErrors.originAddress}
                  hideDropdownButton
                  minLettersForSearch={MIN_LETTERS_TO_SEARCH}
                />
              </div>
              <div className={styles.shipmentEntryInputGroup__originDest}>
                <AddressAutoComplete
                  placeholder="Address, city, or zip code"
                  onChange={handleDestinationAddressChange}
                  name="destination"
                  transparentBg={false}
                  label="Destination Address"
                  addressType="mailing"
                  sameAsMailing
                  status={fieldErrors.destinationAddress ? "error" : ""}
                  errorMessage={fieldErrors.destinationAddress}
                  hideDropdownButton
                  minLettersForSearch={MIN_LETTERS_TO_SEARCH}
                />
              </div>
            </div>
          )}

          {transportationMode === TRANSPORTATION_MODES.oceanFcl ||
          transportationMode === TRANSPORTATION_MODES.oceanLcl ? (
            <>
              <div className={styles.shipmentEntryInputGroup__weightDropdown}>
                <SplitDropdown
                  label={"Shipment Weight/TEUs"}
                  placeholder="unit"
                  inputPlaceholder="Enter weight/TEUs"
                  options={OCEAN_WEIGHT_UNIT_OPTIONS}
                  onInputChange={handleWeightValueChange}
                  onDropdownChange={handleWeightUnitChange}
                  name="weight"
                  type="text"
                  status={fieldErrors.weight ? "error" : ""}
                  errorMessage={fieldErrors.weight}
                  value={data.weight.unit}
                  tooltipContent={weightTooltipContent}
                />
              </div>
              <Dropdown
                label="Reefers"
                placeholder="Select"
                options={REEFER_OPTIONS}
                onChange={handleReeferChange}
                name="reefer"
                transparentBg={false}
                value={data.isReefer ? "true" : "false"}
                tooltipContent={reeferTooltipContent}
                classes={styles.shipmentEntryInputGroup__reeferDropdown}
              />
              <Dropdown
                label="Inland"
                placeholder="Select"
                options={IS_INLAND_OPTIONS}
                onChange={handleInlandChange}
                name="inland"
                transparentBg={false}
                value={data.isInland ? "true" : "false"}
                tooltipContent={inlandTooltipContent}
              />
            </>
          ) : (
            <div className={styles.shipmentEntryInputGroup__weightDropdown}>
              <SplitDropdown
                label={"Shipment Weight"}
                placeholder="unit"
                inputPlaceholder="Enter weight"
                options={WEIGHT_UNIT_OPTIONS}
                onInputChange={handleWeightValueChange}
                onDropdownChange={handleWeightUnitChange}
                name="weight"
                type="text"
                status={fieldErrors.weight ? "error" : ""}
                errorMessage={fieldErrors.weight}
                value={data.weight.unit}
              />
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default ShipmentEntryInputGroup;
